/* BlueCollarPreview.jsx — Path #6 full preview page (USER view).
 *
 * MIRRORS THE ADMIN /explore/careers-ts-ap/blue-collar/:id PAGE EXACTLY —
 * same hero, same sticky pill nav, same 12 sections (Why It Matters /
 * Daily Work / Skills / Tools / Training / Earnings / Growth / Own-Business
 * Path / AI & Automation / How to Start / Safety & Trust / Quiz), same
 * renderers, labels, accents, icons, and copy.
 *
 * The ONLY difference from admin BlueCollarCareerDetail.jsx:
 *   • Fetches a single career from /api/blue-collar/{career_id} (gate-
 *     protected — only kept-by-user or admin). The 173-career JSON is
 *     never sent to the browser.
 *   • Back-link points to /blue-collar.
 *   • No prev/next adjacent nav (user has only 3 picks).
 *   • No PreviewUnlockOverlay / QuotaBanner (user is past the gate).
 *
 * Every field that exists on the record is rendered. Admin code is NOT
 * touched.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { recordCardEvent } from '../lib/clarityCard';
import {
  ArrowLeft, Loader2, Quote, Briefcase, Wrench, BookOpen, TrendingUp,
  Building2, Bot, ShieldAlert, Smartphone,
  Brain, Heart, DollarSign, GraduationCap, Award, Sparkles,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  Section, InfoCard, ChipList, PillNav, QuizYesNoMcq, Bullets,
} from '../components/preview-ui/PreviewPrimitives';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'respect',     label: 'Why It Matters',      icon: Heart },
  { id: 'daily',       label: 'Daily Work',          icon: Briefcase },
  { id: 'skills',      label: 'Skills',              icon: Wrench },
  { id: 'tools',       label: 'Tools & Products',    icon: BookOpen },
  { id: 'training',    label: 'Training',            icon: GraduationCap },
  { id: 'earnings',    label: 'Earnings',            icon: DollarSign },
  { id: 'growth',      label: 'Growth Path',         icon: TrendingUp },
  { id: 'business',    label: 'Own Business Path',   icon: Building2 },
  { id: 'ai-outlook',  label: 'AI & Automation',     icon: Bot },
  { id: 'platform',    label: 'Start on Platform',   icon: Smartphone },
  { id: 'safety',      label: 'Safety & Trust',      icon: ShieldAlert },
  { id: 'quiz',        label: 'Quiz',                icon: Brain },
  { id: 'ai-toolkit',  label: 'AI Toolkit',          icon: Sparkles },
];

export default function BlueCollarPreview() {
  const { careerId } = useParams();
  const [career, setCareer] = useState(null);
  const [toolkit, setToolkit] = useState(null);
  const [register, setRegister] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, [careerId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(''); setCareer(null);
    (async () => {
      try {
        const [r, tRes] = await Promise.all([
          axios.get(`${API}/api/blue-collar/${careerId}`),
          axios.get(`${API}/api/blue-collar/ai-toolkit`).catch(() => ({ data: {} })),
        ]);
        if (!cancelled) {
          setCareer(r.data || null);
          if (r.data) recordCardEvent('preview_explored', { preview_id: `blue_collar:${careerId}`, name: r.data.name || r.data.title });
          setToolkit(tRes.data?.toolkit || null);
          setRegister(tRes.data?.verification_register || null);
        }
      } catch (e) {
        if (cancelled) return;
        const st = e?.response?.status;
        if (st === 404) setErr('This preview is being added.');
        else if (st === 403) setErr('Open this preview from your picks.');
        else if (st === 401) setErr('Your session expired — please reload the page.');
        else setErr(e?.response?.data?.detail || 'Could not load this preview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [careerId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[70vh]" data-testid="bc-preview-loading">
          <Loader2 className="w-10 h-10 text-teal-600 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (err || !career) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md" data-testid="bc-preview-error">
            <p className="text-2xl font-bold text-gray-900 mb-2">
              {err || 'This preview is being added.'}
            </p>
            <Button asChild className="mt-4 bg-amber-600 hover:bg-amber-700">
              <Link to="/blue-collar">Back to Blue-Collar Jobs</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9F1]">
      <Navbar />

      {/* Hero — mirrors admin BlueCollarCareerDetail.jsx */}
      <section className="relative pt-32 md:pt-36 pb-12 px-4 md:px-6 bg-gradient-to-br from-teal-500 via-emerald-500 to-cyan-600 text-white rounded-b-[2.5rem] overflow-hidden">
        <div className="absolute -top-12 right-8 w-48 h-48 rounded-full bg-white/10" aria-hidden="true" />
        <div className="absolute bottom-0 -left-10 w-36 h-36 rounded-full bg-white/10" aria-hidden="true" />
        <div className="max-w-4xl mx-auto relative">
          <Link
            to="/blue-collar"
            data-testid="bc-preview-back"
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-5 transition-colors font-semibold"
          >
            <ArrowLeft size={14} /> Back to Blue-Collar Jobs
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-4 text-xs font-bold">
            <span className="bg-white/25 backdrop-blur px-3 py-1 rounded-full" data-testid="bc-preview-id">
              #{String(career.id).padStart(3, '0')}
            </span>
            {career.category && (
              <span className="bg-white/15 backdrop-blur px-3 py-1 rounded-full" data-testid="bc-preview-category">
                {career.category}
              </span>
            )}
            {career.platform && (
              <span className="bg-white/15 backdrop-blur px-3 py-1 rounded-full" data-testid="bc-preview-platform">
                via {career.platform}
              </span>
            )}
          </div>
          <h1
            data-testid="bc-preview-title"
            className="text-3xl md:text-5xl font-black leading-tight mb-2"
          >
            {career.title}
          </h1>
          {career.headline && career.headline !== career.title && (
            <p className="text-lg md:text-xl text-white/90 font-medium mb-5" data-testid="bc-preview-headline">
              {career.headline}
            </p>
          )}
          {career.description && (
            <blockquote
              className="relative bg-white/10 backdrop-blur border border-white/20 rounded-3xl px-5 py-5 md:px-7 md:py-6"
              data-testid="bc-preview-description"
            >
              <Quote size={20} className="text-amber-300 mb-2.5" />
              <p className="text-base md:text-lg text-white leading-relaxed italic">{career.description}</p>
            </blockquote>
          )}
        </div>
      </section>

      <PillNav sections={SECTIONS} testidPrefix="bc-preview-nav" />

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-6">

        {/* Respect / why it matters */}
        {career.respectMessage && (
          <Section id="respect" icon={Heart} title="Why This Work Matters" accent="rose">
            <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 md:p-5">
              <div className="flex items-start gap-3">
                <Award size={22} className="text-rose-600 shrink-0 mt-0.5" />
                <p className="text-[15px] md:text-base text-gray-800 leading-relaxed font-medium">
                  {career.respectMessage}
                </p>
              </div>
            </div>
          </Section>
        )}

        {/* Daily work */}
        {career.dailyWork && (
          <Section id="daily" icon={Briefcase} title="Daily Work" accent="teal">
            <p>{career.dailyWork}</p>
          </Section>
        )}

        {/* Skills */}
        {career.skillsToBuild?.length > 0 && (
          <Section id="skills" icon={Wrench} title="Skills to Build" accent="teal">
            <ChipList items={career.skillsToBuild} tone="teal" />
          </Section>
        )}

        {/* Tools / products */}
        {career.toolsAndProducts?.length > 0 && (
          <Section id="tools" icon={BookOpen} title="Tools & Products" accent="blue">
            <ChipList items={career.toolsAndProducts} tone="blue" />
          </Section>
        )}

        {/* Training */}
        {(career.trainingAndCertification || career.verifiedRequirementsAndSchemes?.length > 0) && (
          <Section id="training" icon={GraduationCap} title="Training & Certification" accent="indigo">
            {career.trainingAndCertification && <p>{career.trainingAndCertification}</p>}
            {/* verifiedRequirementsAndSchemes — safety-critical verified facts */}
            {career.verifiedRequirementsAndSchemes?.length > 0 && (
              <div className="mt-3" data-testid="bc-preview-verified-reqs">
                <InfoCard label="Verified requirements & schemes" tone="rose">
                  <Bullets items={career.verifiedRequirementsAndSchemes} tone="rose" />
                </InfoCard>
              </div>
            )}
          </Section>
        )}

        {/* Earnings — clearly labelled INDICATIVE */}
        {career.earningPatternIndia && (
          <Section id="earnings" icon={DollarSign} title="Earning Pattern (India)" accent="emerald">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 md:p-5">
              <div className="flex items-start gap-3">
                <DollarSign size={22} className="text-emerald-600 shrink-0 mt-0.5" />
                <p className="text-[15px] text-gray-800 leading-relaxed">{career.earningPatternIndia}</p>
              </div>
              <p className="mt-3 text-[12px] uppercase font-bold tracking-widest text-emerald-700">
                Indicative — confirm on the platform
              </p>
            </div>
            {/* earningSourcedNote + earningSources — next to the existing earnings field */}
            {(career.earningSourcedNote || career.earningSources?.length > 0) && (
              <div className="mt-3" data-testid="bc-preview-earning-sources">
                <InfoCard label="Sourced earnings anchor" tone="mint">
                  {career.earningSourcedNote}
                  {career.earningSources?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {career.earningSources.map((s, i) => (
                        <li key={i} className="text-sm break-all">
                          <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:text-emerald-900 underline">{s.title}</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </InfoCard>
              </div>
            )}
          </Section>
        )}

        {/* Growth path */}
        {career.growthPath && (
          <Section id="growth" icon={TrendingUp} title="Growth Path" accent="cyan">
            <p>{career.growthPath}</p>
          </Section>
        )}

        {/* Independence / business */}
        {career.independenceAndBusinessPath && (
          <Section id="business" icon={Building2} title="Independence & Own-Business Path" accent="amber">
            <InfoCard label="Build your own thing" tone="cream">
              {career.independenceAndBusinessPath}
            </InfoCard>
          </Section>
        )}

        {/* AI & automation outlook */}
        {(career.aiAndAutomationOutlook || career.ai_edge_for_this_work) && (
          <Section id="ai-outlook" icon={Bot} title="AI & Automation Outlook" accent="purple">
            {career.aiAndAutomationOutlook && (
              <InfoCard label="What stays human" tone="violet">
                {career.aiAndAutomationOutlook}
              </InfoCard>
            )}
            {/* ai_edge_for_this_work — per career */}
            {career.ai_edge_for_this_work && (
              <div className="mt-3" data-testid="bc-preview-ai-edge">
                <InfoCard label="AI edge for this work" tone="sky">
                  {career.ai_edge_for_this_work}
                </InfoCard>
              </div>
            )}
          </Section>
        )}

        {/* How to start on platform */}
        {(career.howToStartOnPlatform || career.platformVerification?.status) && (
          <Section
            id="platform"
            icon={Smartphone}
            title={`How to Start on ${career.platform || 'the Platform'}`}
            accent="indigo"
          >
            {career.howToStartOnPlatform && <p>{career.howToStartOnPlatform}</p>}
            {/* platformVerification — near the platform name */}
            {career.platformVerification?.status && (
              <div className="mt-3" data-testid="bc-preview-platform-verification">
                <InfoCard
                  label={`Platform verification: ${career.platformVerification.status}${career.platformVerification.checked ? ` · Checked ${career.platformVerification.checked}` : ''}`}
                  tone={career.platformVerification.status === 'verified_active' ? 'mint' : 'cream'}
                >
                  {career.platformVerification.fact}
                  {career.platformVerification.url && (
                    <p className="mt-2 text-sm break-all">
                      <a href={career.platformVerification.url} target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:text-teal-900 underline">{career.platformVerification.url}</a>
                    </p>
                  )}
                </InfoCard>
              </div>
            )}
          </Section>
        )}

        {/* Safety & trust */}
        {career.safetyTrustAndProfessionalism && (
          <Section id="safety" icon={ShieldAlert} title="Safety, Trust & Professionalism" accent="rose">
            <InfoCard label="Non-negotiables" tone="rose">
              {career.safetyTrustAndProfessionalism}
            </InfoCard>
          </Section>
        )}

        {/* Quiz */}
        {career.quiz && (
          <Section id="quiz" icon={Brain} title="Career Fit Quiz — Try It" accent="purple">
            <QuizYesNoMcq quiz={career.quiz} testidPrefix="bc-preview-quiz" />
          </Section>
        )}

        {/* ai_toolkit_for_workers + verification_register — catalog-level */}
        {toolkit && Object.keys(toolkit).length > 0 && (
          <Section id="ai-toolkit" icon={Sparkles} title="AI Toolkit for Workers" accent="teal">
            <div className="space-y-4" data-testid="bc-preview-ai-toolkit">
              {toolkit.headline && <p>{toolkit.headline}</p>}

              {Array.isArray(toolkit.free_tools_you_can_use_today) && toolkit.free_tools_you_can_use_today.length > 0 && (
                <div data-testid="bc-toolkit-tools">
                  <p className="text-[11px] uppercase font-bold tracking-[0.18em] text-teal-800 mb-2">Free tools you can use today</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {toolkit.free_tools_you_can_use_today.map((t, i) => (
                      <InfoCard key={i} label={t.tool} tone="teal">{t.use}</InfoCard>
                    ))}
                  </div>
                </div>
              )}

              {toolkit.how_platform_ai_already_affects_you && (
                <InfoCard label="How platform AI already affects you" tone="sky">
                  {toolkit.how_platform_ai_already_affects_you}
                </InfoCard>
              )}

              {toolkit.one_serious_warning && (
                <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 md:p-5" data-testid="bc-toolkit-warning">
                  <div className="flex items-start gap-3">
                    <ShieldAlert size={22} className="text-rose-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[11px] uppercase font-bold tracking-[0.18em] text-rose-800 mb-1.5">One serious warning</p>
                      <p className="text-[15px] text-gray-800 leading-relaxed font-medium">{toolkit.one_serious_warning}</p>
                    </div>
                  </div>
                </div>
              )}

              {toolkit.the_big_picture && (
                <InfoCard label="The big picture" tone="cream">{toolkit.the_big_picture}</InfoCard>
              )}

              {register && Object.keys(register).length > 0 && (
                <InfoCard label={`Verification register${register.verified_on ? ` · Verified on ${register.verified_on}` : ''}`} tone="mint">
                  <div data-testid="bc-preview-verification-register">
                    {register.method && <p>{register.method}</p>}
                    {Array.isArray(register.verified_facts) && register.verified_facts.length > 0 && (
                      <div className="mt-2">
                        <Bullets items={register.verified_facts} tone="emerald" />
                      </div>
                    )}
                    {Array.isArray(register.sources) && register.sources.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {register.sources.map((s, i) => (
                          <li key={i} className="text-sm break-all">
                            <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:text-emerald-900 underline">{s.title}</a>
                          </li>
                        ))}
                      </ul>
                    )}
                    {register.standing_warning && <p className="mt-3 font-medium">{register.standing_warning}</p>}
                  </div>
                </InfoCard>
              )}

              {toolkit.added && (
                <p className="text-[12px] uppercase font-bold tracking-widest text-emerald-700">Section added: {toolkit.added} · Confirm current terms on the platform</p>
              )}
            </div>
          </Section>
        )}

      </main>

      <Footer />
    </div>
  );
}
